import * as React from 'react'

const TRANSCRIBE_URL = "https://landscapes.wearepal.ai/api/v2/transcribe"

/**
 * Upload a recorded audio blob to the Landscapes Whisper endpoint and return
 * the transcribed text. The API expects multipart field `audio` and responds
 * with `{ text: string }`.
 */
export async function transcribeAudio(blob: Blob): Promise<string> {
  const form = new FormData()
  const extension = blob.type.includes("ogg")
    ? "ogg"
    : blob.type.includes("mp4") || blob.type.includes("m4a")
      ? "m4a"
      : blob.type.includes("wav")
        ? "wav"
        : "webm"
  form.append("audio", blob, `recording.${extension}`)

  const response = await fetch(TRANSCRIBE_URL, {
    method: "POST",
    body: form,
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => "")
    throw new Error(
      detail
        ? `Transcription failed (${response.status}): ${detail}`
        : `Transcription failed (${response.status})`
    )
  }
  const data = (await response.json()) as { text?: unknown }
  if (typeof data.text !== "string") {
    throw new Error("Transcription response was missing text.")
  }
  return data.text.trim()
}

/** Prefer a MediaRecorder MIME type the browser actually supports. */
export function preferredAudioMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ]
  return candidates.find(type => MediaRecorder.isTypeSupported(type))
}

/**
 * Minimal live level meter for an active mic stream — a short row of
 * equaliser-style bars driven by a few frequency bands.
 */
export function RecordingSpectrogram({ stream }: { stream: MediaStream }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const cssW = 120
    const cssH = 22
    const barCount = 12
    const gap = 2
    const barW = (cssW - gap * (barCount - 1)) / barCount
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.floor(cssW * dpr)
    canvas.height = Math.floor(cssH * dpr)
    canvas.style.width = `${cssW}px`
    canvas.style.height = `${cssH}px`
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const paintBars = (levels: number[]) => {
      ctx.clearRect(0, 0, cssW, cssH)
      ctx.fillStyle = "rgba(255, 255, 255, 0.85)"
      for (let i = 0; i < barCount; i++) {
        const level = levels[i] ?? 0.08
        const h = Math.max(2, level * cssH)
        const x = i * (barW + gap)
        const y = (cssH - h) / 2
        ctx.fillRect(x, y, barW, h)
      }
    }

    if (reducedMotion) {
      paintBars(Array.from({ length: barCount }, (_, i) => 0.2 + 0.35 * Math.abs(Math.sin(i))))
      return
    }

    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const audioCtx = new AudioCtx()
    const source = audioCtx.createMediaStreamSource(stream)
    const analyser = audioCtx.createAnalyser()
    analyser.fftSize = 128
    analyser.smoothingTimeConstant = 0.7
    source.connect(analyser)

    const freqData = new Uint8Array(analyser.frequencyBinCount)
    let raf = 0
    let running = true

    const paint = () => {
      if (!running) return
      analyser.getByteFrequencyData(freqData)
      const levels: number[] = []
      const binsPerBar = Math.max(1, Math.floor(freqData.length * 0.5 / barCount))
      for (let i = 0; i < barCount; i++) {
        let sum = 0
        const start = i * binsPerBar
        for (let j = 0; j < binsPerBar; j++) sum += freqData[start + j] ?? 0
        levels.push(Math.min(1, (sum / binsPerBar / 255) * 1.4))
      }
      paintBars(levels)
      raf = requestAnimationFrame(paint)
    }

    void audioCtx.resume().then(() => {
      if (running) paint()
    })

    return () => {
      running = false
      cancelAnimationFrame(raf)
      try {
        source.disconnect()
      } catch {
        /* already disconnected */
      }
      void audioCtx.close()
    }
  }, [stream])

  return (
    <canvas
      ref={canvasRef}
      className="model-editor-chat-spectrogram"
      width={120}
      height={22}
      aria-hidden
    />
  )
}

